import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { ServiceProps } from "common/";
import imgLoading from "asset/img/loading.gif";
import { reducerType } from "redux/reducers";
import styles from "./styles";
import clsx from "clsx";
import { tranformSchedulesForm } from "common/helpers";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import Grid from "@material-ui/core/Grid";
import SubmitButton from "components/Button";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  REJECT_DRAFT_SERVICE_REQUEST,
  MARK_VALIDATED_SERVICE_REQUEST,
} from "redux/reducers/service/actionTypes";

interface MSValidatedServiceProps {
  data: ServiceProps;
  openUrl: Function;
  approval?: boolean | false;
  handleCheck?: Function;
}

const MSValidatedService = React.memo((props: MSValidatedServiceProps) => {
  const { data, approval } = props;
  const translate = useTranslation().t;
  const {
    name,
    description,
    contactEmail,
    address1,
    address2,
    city,
    state,
    zip,
    country,
    phone,
    website,
    youtube,
    facebook,
    instagram,
    twitter,
    type,
    serviceSummary,
    category,
    isAllowedPet,
    age,
    schedules,
    closeSchedules,
    isApproved,
    userEmail,
    draftCreatedBy,
    lastValidatedAt,
    createdAt,
    updatedAt,
  } = data;

  // Draft data fields (may not be in ServiceProps type)
  const draftCreatedAt = (data as any).draftCreatedAt;
  const draftData = (data as any).draftData;
  const address = { address1, city, state, zip };
  const lastAddress = Object.keys(address).map((ad) => `${address[ad]}`);
  const lastSchedules = schedules && tranformSchedulesForm(schedules);
  const lastCloseSchedules =
    closeSchedules && tranformSchedulesForm(closeSchedules);
  let toLang = JSON.parse(localStorage.getItem("@shelter_language"))
    ? JSON.parse(localStorage.getItem("@shelter_language"))
    : "en";
  dayjs.locale(toLang);
  const dates = [createdAt, updatedAt, lastValidatedAt].filter(Boolean);
  const latestDate =
    dates.length > 0
      ? dates.reduce((a, b) => (dayjs(a).isAfter(dayjs(b)) ? a : b))
      : null;

  const classes = styles();
  const dispatch = useDispatch();
  const validatingServiceId = useSelector(
    (state: reducerType) => state.service.validatingServiceId
  );
  const validatingAction = useSelector(
    (state: reducerType) => state.service.validatingAction
  );
  const isThisCardValidating = validatingServiceId === data.id;
  const isApproving = isThisCardValidating && validatingAction === "approve";
  const isRejecting = isThisCardValidating && validatingAction === "reject";

  const handleApprove = (serviceId: string) => {
    dispatch({ type: MARK_VALIDATED_SERVICE_REQUEST, id: serviceId });
  };

  const handleReject = (serviceId: string) => {
    dispatch({ type: REJECT_DRAFT_SERVICE_REQUEST, id: serviceId });
  };

  const hasDraftData = draftData != null;
  /// Check data changes
  const ignoredDraftLocaleFields = ["ageEs", "serviceSummaryEs", "descriptionEs"];
  const hasDraftDataChanged =
    hasDraftData &&
    Object.keys(draftData).some(
      (field) => !ignoredDraftLocaleFields.includes(field)
    );

  // Draft: same field structure as main content; empty draft fields fall back to original data
  const draftAddress =
    hasDraftData && draftData
      ? {
          address1:
            draftData.address1 != null && draftData.address1 !== ""
              ? draftData.address1
              : address1,
          city:
            draftData.city != null && draftData.city !== ""
              ? draftData.city
              : city,
          state:
            draftData.state != null && draftData.state !== ""
              ? draftData.state
              : state,
          zip:
            draftData.zip != null && draftData.zip !== "" ? draftData.zip : zip,
        }
      : null;
  const draftLastAddress = draftAddress
    ? Object.keys(draftAddress)
        .map((ad) => (draftAddress as any)[ad])
        .filter(Boolean)
    : [];
  const draftSchedules =
    hasDraftData && draftData && draftData.schedules
      ? tranformSchedulesForm(draftData.schedules)
      : [];
  const draftCloseSchedules =
    hasDraftData && draftData && draftData.closeSchedules
      ? tranformSchedulesForm(draftData.closeSchedules)
      : [];

  return (
    <div className={clsx(classes.borderItem, classes.marginTop)}>
      <Grid container spacing={1}>
        {/* Main Content */}
        <Grid item xs={12} md={hasDraftData ? 6 : 12}>
          <div className={classes.borderItem}>
            <div className={classes.name}>
              <p className={classes.topLeft}>
                {name}
                {latestDate && (
                  <span style={{ marginLeft: 4 }}>
                    ({dayjs(latestDate).format("MM/DD/YYYY")})
                  </span>
                )}
              </p>
            </div>
            {!!userEmail && (
              <p>
                {translate("ADDED_BY")}: {userEmail}
              </p>
            )}
            <p>
              {translate("ADDRESS")}: {lastAddress.join(", ")}
            </p>
            {draftData != null && draftData.address2 && address2 != null && (
              <p>
                {translate("ADDRESS_2")}: {address2}
              </p>
            )}
            <p>
              {translate("COUNTRY")}: {country}
            </p>
            <p>
              {translate("PHONE")}: {phone}
            </p>
            <p>
              {translate("EMAIL")}: {contactEmail}
            </p>
            <p>
              {translate("WEBSITE")}: {website}
            </p>
            {draftData != null && draftData.youtube && youtube != null && (
              <p>
                {translate("YOUTUBE")}: {youtube}
              </p>
            )}
            {draftData != null && draftData.facebook != null && (
              <p>
                {translate("FACEBOOK")}: {facebook}
              </p>
            )}
            {draftData != null && draftData.instagram != null && (
              <p>
                {translate("INSTAGRAM")}: {instagram}
              </p>
            )}
            {draftData != null && draftData.twitter != null && (
              <p>
                {translate("TWITTER")}: {twitter}
              </p>
            )}
            <p>
              {translate("SERVICES")}: {type.join(", ")}
            </p>
            <p>
              {translate("SERVICE_TYPE")}: {serviceSummary}
            </p>
            <p>
              {translate("CATEGORIES")}: {category.join(", ")}
            </p>
            <p>
              {translate("IS_ALLOWED_PETS")}:{" "}
              {isAllowedPet ? translate("YES") : translate("NO")}
            </p>
            <p>
              {translate("AGE_GROUP")}: {age}
            </p>
            {lastSchedules.length > 0 && (
              <>
                <p>{translate("SCHEDULE")}:</p>
                {lastSchedules.map((s, idx) => (
                  <p key={idx}>
                    <span className={classes.w30}>{s.title}</span>
                    <span className={classes.w70}>{s.date}</span>
                  </p>
                ))}
              </>
            )}
            {lastCloseSchedules.length > 0 && (
              <>
                <p>{translate("CLOSED_DAYS")}:</p>
                {lastCloseSchedules.map((s, idx) => (
                  <p key={idx}>
                    <span className={classes.w30}>{s.title}</span>
                    <span className={classes.w70}>{s.date}</span>
                  </p>
                ))}
              </>
            )}
            <br />
            <p>
              {translate("COMMENT")}: {description}
            </p>
          </div>
        </Grid>

        {/* Draft Data Section */}
        {hasDraftData && draftData && (
          <Grid item xs={12} md={6}>
            <div className={clsx(classes.borderItem, classes.draftSection)}>
              <div className={classes.draftHeader}>
                <p>
                  <strong>{translate("VALIDATED_BY")}:</strong>{" "}
                  {draftCreatedBy.email}
                </p>
                <p>
                  <strong>{translate("CREATED_AT")}:</strong>{" "}
                  {dayjs(draftCreatedAt).format("MM/DD/YYYY HH:mm")}
                </p>
              </div>

              <div className={classes.draftDataContent}>
                {draftData.name != null && draftData.name !== "" && (
                  <div className={classes.name}>
                    <p className={classes.topLeft}>{draftData.name}</p>
                  </div>
                )}
                {draftLastAddress.join(", ") !== lastAddress.join(", ") && (
                  <p>
                    {translate("ADDRESS")}: {draftLastAddress.join(", ")}
                  </p>
                )}
                {draftData.address2 != null && (
                  <p>
                    {translate("ADDRESS_2")}: {draftData.address2}
                  </p>
                )}
                {draftData.country != null && (
                  <p>
                    {translate("COUNTRY")}: {draftData.country}
                  </p>
                )}
                {draftData.phone != null && draftData.phone !== "" && (
                  <p>
                    {translate("PHONE")}: {draftData.phone}
                  </p>
                )}
                {draftData.contactEmail != null &&
                  draftData.contactEmail !== "" && (
                    <p>
                      {translate("EMAIL")}: {draftData.contactEmail}
                    </p>
                  )}
                {draftData.website != null && draftData.website !== "" && (
                  <p>
                    {translate("WEBSITE")}: {draftData.website}
                  </p>
                )}
                {draftData.youtube != null && (
                  <p>
                    {translate("YOUTUBE")}: {draftData.youtube}
                  </p>
                )}
                {draftData.facebook != null && (
                  <p>
                    {translate("FACEBOOK")}: {draftData.facebook}
                  </p>
                )}
                {draftData.instagram != null && (
                  <p>
                    {translate("INSTAGRAM")}: {draftData.instagram}
                  </p>
                )}
                {draftData.twitter != null && (
                  <p>
                    {translate("TWITTER")}: {draftData.twitter}
                  </p>
                )}
                {draftData.type != null &&
                  Array.isArray(draftData.type) &&
                  draftData.type.length > 0 && (
                    <p>
                      {translate("SERVICES")}: {draftData.type.join(", ")}
                    </p>
                  )}
                {draftData.serviceSummary != null &&
                  draftData.serviceSummary !== "" && (
                    <p>
                      {translate("SERVICE_TYPE")}: {draftData.serviceSummary}
                    </p>
                  )}
                {draftData.category != null &&
                  Array.isArray(draftData.category) &&
                  draftData.category.length > 0 && (
                    <p>
                      {translate("CATEGORIES")}: {draftData.category.join(", ")}
                    </p>
                  )}
                {draftData.isAllowedPet != null && (
                  <p>
                    {translate("IS_ALLOWED_PETS")}:{" "}
                    {draftData.isAllowedPet
                      ? translate("YES")
                      : translate("NO")}
                  </p>
                )}
                {draftData.age != null && draftData.age !== "" && (
                  <p>
                    {translate("AGE_GROUP")}: {draftData.age}
                  </p>
                )}
                {draftSchedules.length > 0 && (
                  <>
                    <p>{translate("SCHEDULE")}:</p>
                    {draftSchedules.map((s, idx) => (
                      <p key={idx}>
                        <span className={classes.w30}>{s.title}</span>
                        <span className={classes.w70}>{s.date}</span>
                      </p>
                    ))}
                  </>
                )}
                {draftCloseSchedules.length > 0 && (
                  <>
                    <p>{translate("CLOSED_DAYS")}:</p>
                    {draftCloseSchedules.map((s, idx) => (
                      <p key={idx}>
                        <span className={classes.w30}>{s.title}</span>
                        <span className={classes.w70}>{s.date}</span>
                      </p>
                    ))}
                  </>
                )}
                {draftData.description != null && draftData.description !== "" && (
                  <>
                    <p>
                      {translate("COMMENT")}: {draftData.description}
                    </p>
                  </>
                )}
                {!hasDraftDataChanged && (
                  <p>
                    {translate("NO_CHANGES_MADE")}
                  </p>
                )}
              </div>
            </div>
          </Grid>
        )}
      </Grid>

      {/* Both Information - Common for both sections */}
      {!approval && (
        <p
          className={clsx({
            [classes.textApproved]: isApproved,
            [classes.textNotApproved]: !isApproved,
          })}
          style={{ marginTop: 15 }}
        >
          {translate(
            isApproved
              ? "THIS_SERVICE_IS_APPROVED"
              : "THIS_SERVICE_IS_NOT_APPROVED"
          )}
        </p>
      )}

      <Grid
        style={{ paddingTop: "10px", paddingBottom: "10px" }}
        container
        spacing={1}
        direction="row"
      >
        <Grid item xs={6} sm={6} md={6} lg={6}>
          <ButtonGroup fullWidth>
            <SubmitButton
              variant="contained"
              disabled={isThisCardValidating}
              onClick={() => handleApprove(data.id)}
            >
              {isApproving ? (
                <img alt="loading" src={imgLoading} width="24px" />
              ) : (
                translate("APPROVE_CHANGES")
              )}
            </SubmitButton>
          </ButtonGroup>
        </Grid>
        <Grid item xs={6} sm={6} md={6} lg={6}>
          <ButtonGroup fullWidth>
            <SubmitButton
              disabled={isThisCardValidating}
              onClick={() => handleReject(data.id)}
              variant="contained"
              className={classes.bgRed}
            >
              {isRejecting ? (
                <img alt="loading" src={imgLoading} width="24px" />
              ) : (
                translate("REJECT_CHANGES")
              )}
            </SubmitButton>
          </ButtonGroup>
        </Grid>
      </Grid>
    </div>
  );
});

export default MSValidatedService;
